import { IBookedOffer, IOffer } from "../../Orchestrator/Types/Index";
import { ITripProduct } from "../../Orchestrator/Types/Interface/IProduct";
import { ProductTypeEnum, isShuttleOffer } from "../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * `CDT=<code>` productTags (IMS, INT 2026-10) list the products a ticket may be
 * exchanged to: the same product, plus its premium version for a class upgrade
 * (Comfort Value carries CDT=STV and CDT=PRV). The backend does NOT filter
 * /exchange-offers by them — every product comes back — so the client must.
 */
const CDT_TAG = /^CDT=(.+)$/i;

type Tagged = { productTags?: string[] | null } | null | undefined;

/** The allowed exchange-target codes, or null when the product isn't tagged. */
export function exchangeTargetCodes(productTags: string[] | null | undefined): string[] | null {
    const codes = (productTags ?? [])
        .map((tag) => CDT_TAG.exec(tag?.trim() ?? "")?.[1]?.trim().toUpperCase())
        .filter((code): code is string => !!code);
    return codes.length > 0 ? codes : null;
}

export function admissionProduct(offer: { products?: ITripProduct[] }): ITripProduct | undefined {
    return offer.products?.find((p) => p.type === ProductTypeEnum.ADMISSION_POINT2POINT);
}

/** A train seat on offer: an admission product that isn't a shuttle (ancillary-only offers don't count). */
export function isTrainAdmissionOffer(offer: IOffer): boolean {
    return !!admissionProduct(offer) && !isShuttleOffer(offer);
}

/**
 * The booked admission products behind the tickets being exchanged. Keyed on
 * the request's fulfillmentIds — not "first offer on the trip" — so tickets
 * already exchanged away, seat reservations and shuttles never decide the rule,
 * and each passenger on a mixed-fare leg contributes their own product.
 */
export function bookedProductsForFulfillments(bookedOffers: IBookedOffer[], fulfillmentIds: string[]): ITripProduct[] {
    const ids = new Set(fulfillmentIds);
    return bookedOffers
        .filter((offer) => !isShuttleOffer(offer) && (offer.admissions ?? []).some((a) => ids.has(a.id)))
        .map(admissionProduct)
        .filter((product): product is ITripProduct => !!product);
}

/**
 * Drops exchange offers the booked products can't be exchanged to. With several
 * booked products (mixed fares on one leg) only products ALL of them allow are
 * kept, so it may be none: those passengers exchange separately.
 * ponytail: intersection, not per-passenger offers — a per-pax filter would need
 * the train list to price each passenger on a different product.
 * Shuttle and ancillary-only offers are kept; a train fare must be allowed even
 * when it carries no tags of its own. With no tagged booked product (an env
 * without the tags) the list is left as-is.
 * `allowedExchangeCodes` returns null for "no rule" and [] for "nothing in common".
 */
export function allowedExchangeCodes(bookedProducts: Tagged[]): string[] | null {
    const allowedSets = bookedProducts
        .map((product) => exchangeTargetCodes(product?.productTags))
        .filter((codes): codes is string[] => codes !== null);
    if (allowedSets.length === 0) return null;
    return allowedSets.reduce((acc, codes) => acc.filter((code) => codes.includes(code)));
}

/**
 * Passengers on different products that can't move together without one of
 * them changing product: Comfort Value + Premium Value share only Premium
 * Value, which would force the Comfort passenger into a paid upgrade.
 */
export function isMixedFareExchange(bookedProducts: ITripProduct[]): boolean {
    const allowed = allowedExchangeCodes(bookedProducts);
    if (!allowed) return false;
    return bookedProducts.some((product) => !!exchangeTargetCodes(product.productTags) && !allowed.includes(product.code.toUpperCase()));
}

export function filterExchangeOffersByCdtTags(offers: IOffer[], bookedProducts: Tagged[]): IOffer[] {
    const allowed = allowedExchangeCodes(bookedProducts);
    if (!allowed) return offers;
    return offers.filter((offer) => {
        if (!isTrainAdmissionOffer(offer)) return true;
        return allowed.includes((admissionProduct(offer)?.code ?? "").toUpperCase());
    });
}
